import { type Row, type SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { calculateAgeAtDate, fullStateNameFromAbbreviation } from 'utils'
import { type QuotingEmployee } from 'utils/web_api.schemas'

const getAgeBracket = (age: number): string => {
  if (age < 26) return 'Under 26'
  if (age < 35) return '26-34'
  if (age < 45) return '35-44'
  if (age < 55) return '45-54'
  if (age < 65) return '55-64'
  return '65+'
}

type EmployeeFilterOptions = {
  searchQuery: string
  filteredStates: string[]
  filteredAgeBrackets: string[]
  filteredPayTypes: string[]
  pinnedEmployeeIds?: string[]
}

type EmployeeCounts = {
  employeeCountByState: Record<string, number>
  employeeCountByAgeBrackets: Record<string, number>
  employeeCountByPayType: Record<string, number>
}

export const useEmployeeFilters = (
  employees: QuotingEmployee[],
  options: EmployeeFilterOptions
) => {
  const {
    filteredStates,
    filteredAgeBrackets,
    filteredPayTypes,
  } = options

  const employeeCounts = useMemo((): EmployeeCounts => {
    const countsByState: Record<string, number> = {}
    const countsByAgeBrackets: Record<string, number> = {}
    const countsByPayType: Record<string, number> = {}

    employees.forEach(emp => {
      const stateName = emp.rating_area_code
        ? fullStateNameFromAbbreviation(emp.rating_area_code.substring(0, 2))
        : null

      if (stateName) {
        countsByState[stateName] = (countsByState[stateName] || 0) + 1
      }

      const age = calculateAgeAtDate(emp.date_of_birth, new Date())
      const ageBracket = getAgeBracket(age)
      countsByAgeBrackets[ageBracket] = (countsByAgeBrackets[ageBracket] || 0) + 1

      const payType = emp.pay_type || 'Hourly'
      countsByPayType[payType] = (countsByPayType[payType] || 0) + 1
    })

    return {
      employeeCountByState: countsByState,
      employeeCountByAgeBrackets: countsByAgeBrackets,
      employeeCountByPayType: countsByPayType
    }
  }, [employees])

  const additionalFilters = useMemo(() => {
    return (employee: QuotingEmployee) => {
      if (filteredStates.length > 0) {
        const employeeState = fullStateNameFromAbbreviation(employee.rating_area_code.substring(0, 2))
        if (!employeeState || !filteredStates.includes(employeeState)) {
          return false
        }
      }

      if (filteredPayTypes.length > 0 && !filteredPayTypes.includes(employee.pay_type || '')) {
        return false
      }

      if (filteredAgeBrackets.length > 0) {
        const employeesAge = calculateAgeAtDate(employee.date_of_birth, new Date())
        const ageBracket = getAgeBracket(employeesAge)
        return filteredAgeBrackets.includes(ageBracket)
      }

      return true
    }
  }, [filteredStates, filteredPayTypes, filteredAgeBrackets])

  return {
    employeeCounts,
    additionalFilters
  }
}

export function createCustomSortingFn<T extends { id: string }> (sorting: SortingState, pinnedEmployeeIds: string[]) {
  return (getValue: (employee: T) => number) => {
    return (rowA: Row<T>, rowB: Row<T>, columnId: string): number => {
      const sortingDir = sorting.find(s => s.id === columnId)?.desc ? 'desc' : 'asc'
      const aIsPinned = pinnedEmployeeIds.includes(rowA.original.id)
      const bIsPinned = pinnedEmployeeIds.includes(rowB.original.id)
      if (aIsPinned && !bIsPinned) return sortingDir === 'asc' ? -1 : 1
      if (!aIsPinned && bIsPinned) return sortingDir === 'asc' ? 1 : -1
      if (aIsPinned && bIsPinned) return 0
      const a = getValue(rowA.original)
      const b = getValue(rowB.original)
      return a - b
    }
  }
}

export const sortAgeBrackets = (a: [string, number], b: [string, number]): number => {
  const ageBracketOrder = ['Under 26', '26-34', '35-44', '45-54', '55-64', '65+']
  return ageBracketOrder.indexOf(a[0]) - ageBracketOrder.indexOf(b[0])
}

export const useEmployeeFilterState = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredStates, setFilteredStates] = useState<string[]>([])
  const [filteredAgeBrackets, setFilteredAgeBrackets] = useState<string[]>([])
  const [filteredPayTypes, setFilteredPayTypes] = useState<string[]>([])

  const handleStateFilterChange = (state: string, checked: boolean) => {
    setFilteredStates(prev =>
      checked ? [...prev, state] : prev.filter(s => s !== state)
    )
  }

  const handleAgeBracketFilterChange = (ageBracket: string, checked: boolean) => {
    setFilteredAgeBrackets(prev =>
      checked ? [...prev, ageBracket] : prev.filter(a => a !== ageBracket)
    )
  }

  const handlePayTypeFilterChange = (payType: string, checked: boolean) => {
    setFilteredPayTypes(prev =>
      checked ? [...prev, payType] : prev.filter(p => p !== payType)
    )
  }

  return {
    searchQuery,
    setSearchQuery,
    filteredStates,
    setFilteredStates,
    filteredAgeBrackets,
    setFilteredAgeBrackets,
    filteredPayTypes,
    setFilteredPayTypes,
    handleStateFilterChange,
    handleAgeBracketFilterChange,
    handlePayTypeFilterChange
  }
}
